import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { BookOpen, Check, FileText, FlaskConical, Minus } from "lucide-react";
import { getIngredientPage, type IngredientPage } from "@/lib/ingredientDetail";

const BASE = "https://truwe.in";

function load(slug: string): IngredientPage {
  const page = getIngredientPage(slug);
  if (!page) throw notFound();
  return page;
}

export const Route = createFileRoute("/ingredients/$slug")({
  loader: ({ params }) => ({ page: load(params.slug) }),
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Ingredient not found | TruWe" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const p = loaderData.page;
    const title = `${p.name} form, dose & clinical research | TruWe`;
    const description = `${p.name}: the exact form TruWe uses (${p.detail.form}), why that form, dose per serving, and the published studies behind it.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: p.canonicalUrl },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: p.canonicalUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            name: p.name,
            description: p.detail.whatItIs,
            termCode: p.slug,
            url: p.canonicalUrl,
            inDefinedTermSet: {
              "@type": "DefinedTermSet",
              name: "TruWe Ingredient Library",
              url: `${BASE}/ingredients`,
            },
            sameAs: p.citations.map((c) => c.url),
            subjectOf: p.citations.map((c) => ({
              "@type": "ScholarlyArticle",
              headline: c.title,
              name: c.title,
              datePublished: String(c.year),
              url: c.url,
              isPartOf: { "@type": "Periodical", name: c.journal },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: `${p.name} form, dose and published research`,
            url: p.canonicalUrl,
            description: p.detail.whatItIs,
            about: { "@type": "Substance", name: p.name, description: p.detail.form },
            citation: p.citations.map((c) => ({
              "@type": "ScholarlyArticle",
              headline: c.title,
              name: c.title,
              datePublished: String(c.year),
              url: c.url,
              isPartOf: { "@type": "Periodical", name: c.journal },
            })),
          }),
        },

        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: p.detail.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
              { "@type": "ListItem", position: 2, name: "Ingredients & Research", item: `${BASE}/ingredients` },
              { "@type": "ListItem", position: 3, name: p.name, item: p.canonicalUrl },
            ],
          }),
        },
      ],
    };
  },
  component: IngredientDetailPage,
  notFoundComponent: IngredientNotFound,
});

function IngredientNotFound() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-brand-deep">Ingredient not found</h1>
      <p className="mt-3 text-muted-foreground">
        We don't have a page for that ingredient.
      </p>
      <Link to="/ingredients" className="mt-6 inline-block text-sm font-semibold text-brand hover:underline">
        Browse all ingredients →
      </Link>
    </div>
  );
}

function Section({
  eyebrow,
  title,
  children,
}: {
  eyebrow?: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-12">
      {eyebrow && (
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">{eyebrow}</p>
      )}
      <h2 className="mt-1 font-display text-2xl font-bold text-brand-deep">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function IngredientDetailPage() {
  const { page } = Route.useLoaderData() as { page: IngredientPage };
  const d = page.detail;

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 md:px-8 md:py-16">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/ingredients" className="font-semibold text-brand hover:underline">
          Ingredients &amp; Research
        </Link>
        <span className="mx-2">/</span>
        <span>{page.name}</span>
      </nav>

      <header className="mt-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
          {page.skuLabel} · {page.studyCount}
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-brand-deep md:text-5xl">
          {page.name}
        </h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">{d.whatItIs}</p>
      </header>

      <div className="mt-8 rounded-3xl border border-border bg-brand-soft/50 p-6 md:p-8">
        <div className="flex items-start gap-3">
          <FlaskConical className="mt-1 h-5 w-5 flex-shrink-0 text-brand" />
          <div>
            <h2 className="font-display text-lg font-bold text-brand-deep">The form we use</h2>
            <p className="mt-1 text-sm font-medium text-brand-deep">{d.form}</p>
            <p className="mt-3 text-sm text-muted-foreground">{d.formRationale}</p>
          </div>
        </div>
      </div>

      <Section eyebrow="Why this form" title="How the common forms compare">
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full min-w-[560px] text-left text-sm">
            <caption className="sr-only">
              Comparison of common delivery forms of {page.name} and which TruWe uses
            </caption>
            <thead className="bg-card">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold text-brand-deep">Form</th>
                <th scope="col" className="px-4 py-3 font-semibold text-brand-deep">
                  What published literature reports
                </th>
                <th scope="col" className="px-4 py-3 font-semibold text-brand-deep">In TruWe</th>
              </tr>
            </thead>
            <tbody>
              {d.formComparison.map((row) => (
                <tr key={row.form} className="border-t border-border align-top">
                  <td className="px-4 py-3 font-medium text-brand-deep">{row.form}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row.reported}</td>
                  <td className="px-4 py-3">
                    {row.used === "yes" ? (
                      <span className="inline-flex items-center gap-1 font-semibold text-brand">
                        <Check className="h-4 w-4" aria-hidden="true" /> Used
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <Minus className="h-4 w-4" aria-hidden="true" /> Not used
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section eyebrow="Dose" title="Where it appears, and how much">
        <ul className="grid gap-3 sm:grid-cols-2">
          {d.doseByProduct.map((dp) => (
            <li key={dp.productHandle} className="rounded-2xl border border-border bg-card p-5">
              <p className="font-display text-base font-semibold text-brand-deep">{dp.productTitle}</p>
              <p className="mt-1 text-sm font-semibold uppercase tracking-[0.14em] text-brand">
                {dp.dose}
              </p>
              {dp.doseNote && <p className="text-xs text-muted-foreground">{dp.doseNote}</p>}
              <Link
                to="/products/$handle"
                params={{ handle: dp.productHandle }}
                className="mt-3 inline-block text-xs font-semibold text-brand hover:underline"
              >
                View product →
              </Link>
            </li>
          ))}
        </ul>
      </Section>

      <Section eyebrow="The evidence" title="What the research says">
        <p className="text-sm text-muted-foreground">{page.summary}</p>
        <p className="mt-3 text-xs text-muted-foreground">
          Reference researcher: <span className="font-medium text-brand-deep">{page.scientist}</span>
          {" · "}
          {page.affiliation}
        </p>
        <ul className="mt-5 space-y-3">
          {page.citations.map((c) => (
            <li key={c.url} className="rounded-2xl border border-border bg-card p-4">
              <a
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-brand-deep hover:text-brand"
              >
                {c.title}
              </a>
              <p className="mt-1 text-xs text-muted-foreground">
                {c.journal} · {c.year}
              </p>
            </li>
          ))}
        </ul>
        <div className="mt-5 flex flex-wrap gap-3 text-xs font-semibold">
          <a
            href={page.pdf}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-brand-deep hover:border-brand hover:text-brand"
          >
            <FileText className="h-3 w-3" aria-hidden="true" /> Research summary PDF
          </a>
          {page.clinicalTrials && (
            <a
              href={page.clinicalTrials}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-brand-deep hover:border-brand hover:text-brand"
            >
              <BookOpen className="h-3 w-3" aria-hidden="true" /> ClinicalTrials.gov
            </a>
          )}
        </div>
      </Section>

      <Section eyebrow="Questions, answered" title={`${page.name} FAQ`}>
        <dl className="divide-y divide-border rounded-2xl border border-border bg-card">
          {d.faq.map((f) => (
            <div key={f.q} className="p-5">
              <dt className="font-display text-base font-semibold text-brand-deep">{f.q}</dt>
              <dd className="mt-2 text-sm text-muted-foreground">{f.a}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <p className="mt-12 text-xs text-muted-foreground">
        Ingredient-level research is published on the individual actives and their delivery forms.
        It describes the ingredients, not a finished-product claim. These statements have not been
        evaluated by the FSSAI or FDA. This content is for information only and is not medical
        advice.
      </p>

      <Link
        to="/ingredients"
        className="mt-8 inline-block text-sm font-semibold text-brand hover:underline"
      >
        ← All ingredients
      </Link>
    </div>
  );
}
